'use client';

import {
  Box,
  Button,
  Link,
  Markdown,
  Modal,
  ModalBody,
  ModalFooter,
  Typography
} from '@coolblue-development/freddy';
import clickOnCalculateValueTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeIn/clickOnCalculateValueTrigger';
import deselectOfferTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeIn/deselectOfferTrigger';
import readExplanationTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeIn/readExplanationTrigger';
import showTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeIn/showTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SelectableBox, SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { useContext, useState } from 'react';

import { getData } from './actions';
import { ProductTradeInContext } from './Context';
import EntranceSummary from './EntranceSummary';
import { Actions } from './types';

export const Entrance = ({
  title,
  explanation,
  productTypeId
}: {
  title: string;
  explanation: string;
  productTypeId: number;
}) => {
  const { t } = useTranslation('webshop');
  const modalTranslations = useFreddyTranslations('modal');
  const [isOpen, setIsOpen] = useState(false);
  const { trackEvent } = useAnalytics();
  const productTradeInCheckboxName = 'product-trade-in';
  const { entry, dispatch, summary, entranceChoice } = useContext(ProductTradeInContext);
  const { isMobile } = useBreakpoints();

  async function openTradeInModal() {
    trackEvent(
      clickOnCalculateValueTrigger({
        content: 'trade_in_tool',
        sub_feature: 'not_recognized'
      })
    );

    dispatch?.({ type: Actions.SET_MODAL_OPEN, payload: true });

    if (!entry) {
      dispatch?.({ type: Actions.SET_ENTRY, payload: await getData(productTypeId) });
    }
  }

  function handleExplanationButtonClick() {
    trackEvent(readExplanationTrigger());

    setIsOpen(true);
  }

  function handleExplanationCalculationButton() {
    setIsOpen(false);
    openTradeInModal();
  }

  function handleOnChange(event: string[]) {
    const [firstCheckbox] = event;
    dispatch?.({ type: Actions.SET_ENTRANCE_CHOICE, payload: firstCheckbox === 'true' });
    if (firstCheckbox === 'true') {
      trackEvent(
        clickOnCalculateValueTrigger({
          sub_feature: 'not_recognized',
          content: summary ? 'offer' : 'trade_in_tool'
        })
      );
    } else {
      trackEvent(
        deselectOfferTrigger({
          sub_feature: 'default'
        })
      );
    }
  }

  let handleSelectableClick: (() => Promise<void>) | undefined = openTradeInModal;
  if (summary) {
    handleSelectableClick = summary.tradeInPrice === 0 ? openTradeInModal : undefined;
  }

  return (
    <>
      <TrackImmediate trigger={showTrigger({ sub_feature: 'not_recognized' })} />
      <Typography fontWeight={600} mb={200}>
        {title}
        <Link
          component='button'
          type='button'
          onClick={handleExplanationButtonClick}
          aria-label={t('productTradeIn.explanationButtonAriaLabel')}
          pl={200}
        >
          <Box component='span' hideFrom='md'>
            {t('productTradeIn.explanationButton.mobile')}
          </Box>
          <Box component='span' hideUntil='md'>
            {t('productTradeIn.explanationButton.desktop')}
          </Box>
        </Link>
        <Modal
          translations={modalTranslations}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title={t('productTradeIn.explanationModalTitle')}
          size='large'
          placement={isMobile ? 'bottom' : 'center'}
          body={
            <ModalBody>
              <Markdown>{explanation}</Markdown>
            </ModalBody>
          }
          footer={
            <ModalFooter>
              <Button type='button' onClick={handleExplanationCalculationButton}>
                {t('productTradeIn.explanationModalButton')}
              </Button>
            </ModalFooter>
          }
        />
      </Typography>
      <SelectableBoxGroup
        inputPlacement='left'
        name={productTradeInCheckboxName}
        spacing='default'
        type='checkbox'
        onChange={event => handleOnChange(event)}
        value={[entranceChoice ? 'true' : '']}
      >
        <SelectableBox value='true' onClick={handleSelectableClick}>
          {summary ? (
            <EntranceSummary openTradeInModal={openTradeInModal} />
          ) : (
            t('productTradeIn.discountModalButton')
          )}
        </SelectableBox>
      </SelectableBoxGroup>
    </>
  );
};
